import {
  MarketplaceListingFieldsSchema,
  MarketplaceUploadSchema,
  type MarketplaceUploadKind,
} from "@hisani/marketplace/validation";
import { DateField, Field, Select, TextArea, TextInput } from "@hisani/ui";
import { useQuery } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import React, { useState } from "react";
import { requestApiJson } from "../../api-client";
import { MarketplaceLocationsSchema } from "./marketplace-contracts";
import { formatRegion } from "./marketplace-formatting";

export interface MarketplaceListingFieldValues {
  readonly horseName: string;
  readonly breed: "THOROUGHBRED" | "PUREBRED_ARABIAN" | "OTHER";
  readonly breedOther: string | null;
  readonly dateOfBirth: string;
  readonly sex: "COLT" | "FILLY" | "GELDING" | "MARE" | "STALLION";
  readonly sire: string | null;
  readonly dam: string | null;
  readonly pricingType: "EXACT" | "RANGE" | "CONTACT";
  readonly priceMinor: string | null;
  readonly priceMaxMinor: string | null;
  readonly currency: string;
  readonly countryCode: string;
  readonly city: string;
  readonly horseProfileUrl: string | null;
  readonly description: string | null;
  readonly vetDisclosureNotes: string | null;
}

export type MarketplaceListingSection =
  "horse" | "location" | "pricing" | "description" | "disclosure";

export type MarketplaceListingFieldName =
  | "horseName"
  | "breed"
  | "breedOther"
  | "dateOfBirth"
  | "sex"
  | "sire"
  | "dam"
  | "pricingType"
  | "price"
  | "priceMaximum"
  | "countryCode"
  | "city"
  | "horseProfileUrl"
  | "description"
  | "vetDisclosureNotes";

export type MarketplaceListingFieldErrors = Partial<
  Record<MarketplaceListingFieldName, string>
>;

export function MarketplaceListingFields({
  initialValues,
  sections = ["horse", "location", "pricing", "description", "disclosure"],
  errors = {},
}: {
  readonly initialValues?: Partial<MarketplaceListingFieldValues>;
  readonly sections?: readonly MarketplaceListingSection[];
  readonly errors?: MarketplaceListingFieldErrors;
}) {
  const t = useTranslations("Marketplace");
  const locale = useLocale();
  const [breed, setBreed] = useState(
    initialValues?.breed ?? "PUREBRED_ARABIAN",
  );
  const [countryCode, setCountryCode] = useState<string>(
    initialValues?.countryCode ?? "AE",
  );
  const [pricingType, setPricingType] = useState(
    initialValues?.pricingType ?? "EXACT",
  );
  const locations = useQuery({
    queryKey: ["marketplace", "locations", countryCode],
    queryFn: () =>
      requestApiJson(
        `/api/v1/marketplace/locations?country=${encodeURIComponent(countryCode)}`,
        MarketplaceLocationsSchema,
      ),
    placeholderData: (previous) => previous,
    enabled: sections.includes("location"),
  });
  const countries = locations.data?.countries ?? [];
  const cities: readonly string[] =
    locations.data?.countryCode === countryCode ? locations.data.cities : [];
  const initialCity = cities.some((city) => city === initialValues?.city)
    ? initialValues?.city
    : undefined;

  return (
    <>
      {sections.includes("horse") ? (
        <>
          <MarketplaceTextField
            name="horseName"
            label={t("horseName")}
            minLength={2}
            maxLength={100}
            defaultValue={initialValues?.horseName}
            placeholder={t("horseNameExample")}
            error={errors.horseName}
            required
          />
          <MarketplaceSelectFormField
            name="breed"
            label={t("breed")}
            defaultValue={breed}
            error={errors.breed}
            onValueChange={(value) =>
              setBreed(value as MarketplaceListingFieldValues["breed"])
            }
            required
            options={[
              ["PUREBRED_ARABIAN", t("breedArabian")],
              ["THOROUGHBRED", t("breedThoroughbred")],
              ["OTHER", t("breedOther")],
            ]}
          />
          {breed === "OTHER" ? (
            <MarketplaceTextField
              name="breedOther"
              label={t("otherBreed")}
              minLength={2}
              maxLength={80}
              defaultValue={initialValues?.breedOther ?? undefined}
              error={errors.breedOther}
              required
            />
          ) : null}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <MarketplaceTextField
              name="dateOfBirth"
              label={t("dateOfBirth")}
              type="date"
              defaultValue={initialValues?.dateOfBirth}
              error={errors.dateOfBirth}
              required
            />
            <MarketplaceSelectFormField
              name="sex"
              label={t("sex")}
              defaultValue={initialValues?.sex}
              placeholder={t("chooseSex")}
              error={errors.sex}
              required
              options={[
                ["COLT", t("sexColt")],
                ["FILLY", t("sexFilly")],
                ["GELDING", t("sexGelding")],
                ["MARE", t("sexMare")],
                ["STALLION", t("sexStallion")],
              ]}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <MarketplaceTextField
              name="sire"
              label={t("sire")}
              labelSuffix={t("optional")}
              minLength={2}
              maxLength={100}
              defaultValue={initialValues?.sire ?? undefined}
            />
            <MarketplaceTextField
              name="dam"
              label={t("dam")}
              labelSuffix={t("optional")}
              minLength={2}
              maxLength={100}
              defaultValue={initialValues?.dam ?? undefined}
            />
          </div>
        </>
      ) : null}

      {sections.includes("location") ? (
        <>
          <MarketplaceSelectFormField
            name="countryCode"
            label={t("country")}
            value={countryCode}
            error={errors.countryCode}
            onValueChange={setCountryCode}
            required
            options={countries.map((code) => [
              code,
              formatRegion(code, locale),
            ])}
          />
          <MarketplaceSelectFormField
            key={`${countryCode}-${locations.data?.countryCode ?? "loading"}`}
            name="city"
            label={t("city")}
            defaultValue={initialCity}
            placeholder={t("chooseCity")}
            error={
              errors.city ??
              (locations.isError ? t("locationsUnavailable") : undefined)
            }
            disabled={locations.isFetching || cities.length === 0}
            required
            options={cities.map((city) => [city, city])}
          />
          <MarketplaceTextField
            name="horseProfileUrl"
            label={t("horseProfileUrl")}
            labelSuffix={t("optional")}
            hint={t("horseProfileUrlHint")}
            type="url"
            maxLength={500}
            defaultValue={initialValues?.horseProfileUrl ?? undefined}
            error={errors.horseProfileUrl}
            placeholder="https://www.racingpost.com/profile/horse/..."
          />
        </>
      ) : null}

      {sections.includes("pricing") ? (
        <>
          <MarketplaceSelectFormField
            name="pricingType"
            label={t("pricingOption")}
            defaultValue={pricingType}
            error={errors.pricingType}
            onValueChange={(value) =>
              setPricingType(
                value as MarketplaceListingFieldValues["pricingType"],
              )
            }
            required
            options={[
              ["EXACT", t("pricingExact")],
              ["RANGE", t("pricingRange")],
              ["CONTACT", t("pricingContact")],
            ]}
          />
          {pricingType === "CONTACT" ? (
            <p className="rounded-md border border-border-subtle bg-subtle p-3 text-body-small text-secondary">
              {t("pricingContactHint")}
            </p>
          ) : (
            <div
              className={
                pricingType === "RANGE" ? "grid gap-4 sm:grid-cols-2" : ""
              }
            >
              <MarketplaceTextField
                name="price"
                label={pricingType === "RANGE" ? t("priceMinimum") : t("price")}
                labelSuffix={t("currencyAed")}
                type="number"
                min="0.01"
                max="9999999999999.99"
                step="0.01"
                defaultValue={minorUnitsForInput(initialValues?.priceMinor)}
                placeholder={t("priceExample")}
                error={errors.price}
                required
              />
              {pricingType === "RANGE" ? (
                <MarketplaceTextField
                  name="priceMaximum"
                  label={t("priceMaximum")}
                  labelSuffix={t("currencyAed")}
                  type="number"
                  min="0.01"
                  max="9999999999999.99"
                  step="0.01"
                  defaultValue={minorUnitsForInput(
                    initialValues?.priceMaxMinor,
                  )}
                  placeholder={t("priceExample")}
                  error={errors.priceMaximum}
                  required
                />
              ) : null}
            </div>
          )}
          <input
            type="hidden"
            name="currency"
            value={initialValues?.currency ?? "AED"}
          />
        </>
      ) : null}

      {sections.includes("description") ? (
        <MarketplaceTextarea
          name="description"
          label={t("descriptionLabel")}
          labelSuffix={t("optional")}
          hint={t("descriptionHint")}
          maxLength={5_000}
          rows={5}
          defaultValue={initialValues?.description ?? undefined}
          error={errors.description}
        />
      ) : null}
      {sections.includes("disclosure") ? (
        <MarketplaceTextarea
          name="vetDisclosureNotes"
          label={t("vetDisclosure")}
          labelSuffix={t("optional")}
          hint={t("vetDisclosureHint")}
          minLength={3}
          maxLength={5_000}
          rows={4}
          defaultValue={initialValues?.vetDisclosureNotes ?? undefined}
          error={errors.vetDisclosureNotes}
        />
      ) : null}
    </>
  );
}

function MarketplaceTextField({
  name,
  label,
  type = "text",
  required = false,
  min,
  max,
  minLength,
  maxLength,
  defaultValue,
  step,
  placeholder,
  labelSuffix,
  hint,
  error,
}: {
  readonly name: string;
  readonly label: string;
  readonly type?: "text" | "number" | "date" | "url";
  readonly required?: boolean;
  readonly min?: string;
  readonly max?: string;
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly defaultValue?: string | undefined;
  readonly step?: string;
  readonly placeholder?: string | undefined;
  readonly labelSuffix?: React.ReactNode;
  readonly hint?: React.ReactNode;
  readonly error?: React.ReactNode | undefined;
}) {
  if (type === "date") {
    return (
      <Field label={label} error={error}>
        {(field) => (
          <DateField
            {...field}
            name={name}
            required={required}
            defaultValue={defaultValue}
            className="min-h-12"
          />
        )}
      </Field>
    );
  }
  return (
    <Field label={label} labelSuffix={labelSuffix} hint={hint} error={error}>
      {(field) => (
        <TextInput
          {...field}
          name={name}
          type={type}
          required={required}
          min={min}
          max={max}
          minLength={minLength}
          maxLength={maxLength}
          defaultValue={defaultValue}
          step={step}
          placeholder={placeholder}
          dir={type === "number" || type === "url" ? "ltr" : undefined}
          className="min-h-12"
        />
      )}
    </Field>
  );
}

export function MarketplaceSelectFormField({
  name,
  label,
  options,
  defaultValue,
  value,
  placeholder,
  required = false,
  disabled = false,
  onValueChange,
  error,
}: {
  readonly name: string;
  readonly label: string;
  readonly options: readonly (readonly [string, string])[];
  readonly defaultValue?: string | undefined;
  readonly value?: string | undefined;
  readonly placeholder?: string | undefined;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly onValueChange?: (value: string) => void;
  readonly error?: React.ReactNode | undefined;
}) {
  return (
    <Field label={label} error={error}>
      {(field) => (
        <Select
          {...field}
          className="[&_[role=combobox]]:min-h-12"
          name={name}
          required={required}
          disabled={disabled}
          {...(defaultValue === undefined ? {} : { defaultValue })}
          {...(value === undefined ? {} : { value })}
          {...(onValueChange === undefined ? {} : { onValueChange })}
        >
          {placeholder === undefined ? null : (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map(([value, option]) => (
            <option key={value} value={value}>
              {option}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

function MarketplaceTextarea({
  name,
  label,
  minLength,
  maxLength,
  rows,
  defaultValue,
  labelSuffix,
  hint,
  error,
}: {
  readonly name: string;
  readonly label: string;
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly rows: number;
  readonly defaultValue?: string | undefined;
  readonly labelSuffix?: React.ReactNode;
  readonly hint?: React.ReactNode;
  readonly error?: React.ReactNode | undefined;
}) {
  return (
    <Field label={label} labelSuffix={labelSuffix} hint={hint} error={error}>
      {(field) => (
        <TextArea
          {...field}
          name={name}
          minLength={minLength}
          maxLength={maxLength}
          rows={rows}
          defaultValue={defaultValue}
        />
      )}
    </Field>
  );
}

export function marketplaceListingFieldsFromForm(
  form: FormData,
  invalidMessage: string,
) {
  try {
    const pricingType = requiredText(form, "pricingType");
    return MarketplaceListingFieldsSchema.parse({
      horseName: requiredText(form, "horseName"),
      breed: requiredText(form, "breed"),
      breedOther:
        requiredText(form, "breed") === "OTHER"
          ? optionalText(form, "breedOther")
          : null,
      dateOfBirth: requiredText(form, "dateOfBirth"),
      sex: requiredText(form, "sex"),
      sire: optionalText(form, "sire"),
      dam: optionalText(form, "dam"),
      pricingType,
      priceMinor:
        pricingType === "CONTACT"
          ? null
          : minorUnitsFromInput(requiredText(form, "price")),
      priceMaxMinor:
        pricingType === "RANGE"
          ? minorUnitsFromInput(requiredText(form, "priceMaximum"))
          : null,
      currency: requiredText(form, "currency"),
      countryCode: requiredText(form, "countryCode"),
      city: requiredText(form, "city"),
      horseProfileUrl: optionalText(form, "horseProfileUrl"),
      description: optionalText(form, "description"),
      vetDisclosureNotes: optionalText(form, "vetDisclosureNotes"),
    });
  } catch {
    throw new Error(invalidMessage);
  }
}

export function validateMarketplaceFiles(
  files: readonly File[],
  kind: MarketplaceUploadKind,
  invalidMessage: string,
): void {
  const valid = files.every(
    (file) =>
      MarketplaceUploadSchema.safeParse({
        kind,
        filename: file.name,
        contentType: file.type,
        contentLength: file.size,
      }).success,
  );
  if (!valid) throw new Error(invalidMessage);
}

export function minorUnitsFromInput(value: string): string {
  const match = /^(0|[1-9]\d{0,12})(?:\.(\d{1,2}))?$/u.exec(value);
  if (match?.[1] === undefined) throw new Error("Invalid price.");
  const fraction = (match[2] ?? "").padEnd(2, "0");
  return (BigInt(match[1]) * 100n + BigInt(fraction || "0")).toString();
}

function minorUnitsForInput(
  value: string | null | undefined,
): string | undefined {
  if (value === null || value === undefined) return undefined;
  const minor = BigInt(value);
  const whole = minor / 100n;
  const fraction = (minor % 100n).toString().padStart(2, "0");
  return fraction === "00" ? whole.toString() : `${whole}.${fraction}`;
}

export function requiredText(form: FormData, name: string): string {
  const value = form.get(name);
  if (typeof value !== "string" || value.trim() === "")
    throw new Error(`Missing ${name}.`);
  return value.trim();
}

function optionalText(form: FormData, name: string): string | null {
  const value = form.get(name);
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

export function selectedFiles(form: FormData, name: string): File[] {
  return form
    .getAll(name)
    .filter((value): value is File => value instanceof File && value.size > 0);
}
